/**
 * Label-collision clustering for @googlemaps/markerclusterer. Instead of a
 * fixed SuperCluster radius, price labels that would overlap at the current
 * (possibly fractional) zoom are grouped with `groupStackedLabels` at the size
 * each group actually renders: a short stack of price chips, five chips and a
 * "+N more" row for a crowded shared position, or a count bubble. Pans reuse the
 * cached result (`changed: false`), so nothing re-renders until the zoom or the
 * marker set changes.
 */
import { Cluster, type Algorithm, type AlgorithmInput, type AlgorithmOutput, type Marker } from '@googlemaps/markerclusterer'
import { groupStackedLabels, type LabelGroup } from './mapLabelGroups'
import type { PixelSize } from './mapPopupPlacement'

/** Height of one price chip (`.map-pin.is-price`), CSS px. */
export const CHIP_HEIGHT = 22
/** Gap between stacked chips (`.map-stack`), CSS px. */
export const STACK_GAP = 2
/** Presentation threshold, not a workload limit: most chips stacked for a group that is not tight; larger groups show a count bubble. */
export const STACK_MAX_NEAR = 4
/** Presentation threshold, not a workload limit: most rows for a tight group (members within 30 m, e.g. one postcode centre). */
export const STACK_MAX_TIGHT = 6
/** Size used when a price label measures nothing (detached or unstyled). */
export const PRICE_LABEL_FALLBACK: PixelSize = { width: 60, height: CHIP_HEIGHT }
/** Size used when the count bubble measures nothing. */
export const BUBBLE_FALLBACK: PixelSize = { width: 43, height: 32 }

/** `shown` chips, then a "+`more` more" row when `more` > 0; or one count bubble. */
export type StackPlan = { kind: 'stack'; shown: number; more: number } | { kind: 'bubble' }

/** How a group of `count` labels renders; shared by the collision sizing and the renderer so both agree. */
export function stackPlan(count: number, tight: boolean): StackPlan {
  if (tight) return count <= STACK_MAX_TIGHT ? { kind: 'stack', shown: count, more: 0 } : { kind: 'stack', shown: STACK_MAX_TIGHT - 1, more: count - (STACK_MAX_TIGHT - 1) }
  return count <= STACK_MAX_NEAR ? { kind: 'stack', shown: count, more: 0 } : { kind: 'bubble' }
}

export function stackHeight(rows: number): number {
  return rows * CHIP_HEIGHT + Math.max(0, rows - 1) * STACK_GAP
}

export interface LabelClusterOptions {
  /** Measured CSS px size of a marker's own price label. */
  sizeOf: (marker: Marker) => PixelSize
  /** Stack order, top to bottom; a group's cluster lists its markers in this order. */
  order: (a: Marker, b: Marker) => number
  /** Measured size of a "+N more" chip and of a count bubble. */
  moreSize: PixelSize
  bubbleSize: PixelSize
}

/** AdvancedMarkerElement positions may be LatLng, LatLngAltitude or a literal (MarkerUtils is not exported). */
function positionOf(marker: Marker): { lat: number; lng: number } | null {
  const at = 'getPosition' in marker ? marker.getPosition() : marker.position
  if (!at) return null
  const lat = typeof at.lat === 'function' ? at.lat() : at.lat
  const lng = typeof at.lng === 'function' ? at.lng() : at.lng
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
}

function sameMarkers(a: readonly Marker[], b: readonly Marker[]): boolean {
  return a.length === b.length && a.every((marker, index) => marker === b[index])
}

export class LabelCollisionAlgorithm implements Algorithm {
  /** Increments whenever `calculate` returns a new grouping (`changed: true`). */
  revision = 0
  private readonly options: LabelClusterOptions
  private readonly groups = new WeakMap<Cluster, LabelGroup>()
  private cache: { zoom: number; markers: Marker[]; clusters: Cluster[] } | null = null

  constructor(options: LabelClusterOptions) {
    this.options = options
  }

  /** The clusters of the latest grouping. */
  get clusters(): Cluster[] {
    return this.cache?.clusters ?? []
  }

  /** Tightness and span of a multi-marker cluster from the latest grouping. */
  groupOf(cluster: Cluster): LabelGroup | undefined {
    return this.groups.get(cluster)
  }

  calculate({ markers, map }: AlgorithmInput): AlgorithmOutput {
    const zoom = Math.round((map.getZoom() ?? 0) * 100) / 100
    const cache = this.cache
    if (cache && cache.zoom === zoom && sameMarkers(cache.markers, markers)) return { clusters: cache.clusters, changed: false }
    const clusters = this.group(markers, zoom)
    this.cache = { zoom, markers: [...markers], clusters }
    this.revision += 1
    return { clusters, changed: true }
  }

  private group(markers: readonly Marker[], zoom: number): Cluster[] {
    const { sizeOf, order } = this.options
    const clusters: Cluster[] = []
    const placed: Marker[] = []
    const points = markers.flatMap(marker => {
      const at = positionOf(marker)
      // A marker without a position cannot collide; it stays on its own.
      if (!at) { clusters.push(new Cluster({ markers: [marker] })); return [] }
      const size = sizeOf(marker)
      placed.push(marker)
      return [{ id: String(placed.length - 1), lat: at.lat, lng: at.lng, width: size.width, height: size.height }]
    })
    const membersOf = (ids: string[]): Marker[] => ids.flatMap(id => { const marker = placed[Number(id)]; return marker ? [marker] : [] })
    const groups = groupStackedLabels(points, zoom, { renderedSize: (members, tight) => this.renderedSize(membersOf(members.map(point => point.id)), tight) })
    for (const group of groups) {
      const members = membersOf(group.ids)
      if (members.length === 1) { clusters.push(new Cluster({ markers: members, position: { lat: group.lat, lng: group.lng } })); continue }
      const cluster = new Cluster({ markers: members.sort(order), position: { lat: group.lat, lng: group.lng } })
      this.groups.set(cluster, group)
      clusters.push(cluster)
    }
    return clusters
  }

  /** Stack: widest shown chip by rows * 22 + gaps; bubble: its measured size. */
  private renderedSize(members: Marker[], tight: boolean): PixelSize {
    const plan = stackPlan(members.length, tight)
    if (plan.kind === 'bubble') return this.options.bubbleSize
    const widths = [...members].sort(this.options.order).slice(0, plan.shown).map(marker => this.options.sizeOf(marker).width)
    if (plan.more > 0) widths.push(this.options.moreSize.width)
    return { width: Math.max(0, ...widths), height: stackHeight(widths.length) }
  }
}
